import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import AppShell from './components/AppShell';
import Fleet from './pages/Fleet';
import Login from './pages/Login';
import NewTenant from './pages/NewTenant';
import TenantDetail from './pages/TenantDetail';
import Overview from './pages/tenantDetail/Overview';
import Health from './pages/tenantDetail/Health';
import Pipeline from './pages/tenantDetail/Pipeline';
import Activity from './pages/tenantDetail/Activity';
import UsersTab from './pages/tenantDetail/Users';
import ConfigTab from './pages/tenantDetail/Config';
import AuditTab from './pages/tenantDetail/Audit';
import AssignPlan from './pages/AssignPlan';
import ConnectSlack from './pages/ConnectSlack';
import BaselineConfig from './pages/BaselineConfig';
import InviteAdmin from './pages/InviteAdmin';
import DeliveryInspector from './pages/DeliveryInspector';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { operator, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="text-sm text-slate-500">Loading…</div>
      </div>
    );
  }
  if (!operator) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RedirectIfSignedIn({ children }: { children: React.ReactNode }) {
  const { operator, isLoading } = useAuth();
  if (isLoading) return null;
  if (operator) return <Navigate to="/fleet" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route
          path="/login"
          element={
            <RedirectIfSignedIn>
              <Login />
            </RedirectIfSignedIn>
          }
        />
        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route path="/" element={<Navigate to="/fleet" replace />} />
          <Route path="/fleet" element={<Fleet />} />
          {/* Edit pages sit OUTSIDE the tab shell so they get the full
              main column and skip the tab strip. React Router v6 ranks
              /fleet/:id/plan (fully explicit) above the shell's child
              routes, so these keep working after the Action 49 refactor. */}
          <Route path="/fleet/:id/plan" element={<AssignPlan />} />
          <Route path="/fleet/:id/slack" element={<ConnectSlack />} />
          <Route path="/fleet/:id/baseline" element={<BaselineConfig />} />
          <Route path="/fleet/:id/admin-invite" element={<InviteAdmin />} />
          <Route path="/fleet/:id" element={<TenantDetail />}>
            {/* Bare /fleet/:id → default to overview so a fresh drill-in
                lands somewhere useful. */}
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<Overview />} />
            <Route path="health"   element={<Health />} />
            <Route path="pipeline" element={<Pipeline />} />
            <Route path="activity" element={<Activity />} />
            <Route path="users"    element={<UsersTab />} />
            <Route path="config"   element={<ConfigTab />} />
            <Route path="audit"    element={<AuditTab />} />
          </Route>
          <Route path="/provisioning/new" element={<NewTenant />} />
          {/* Delivery inspector (Action 51) — org-agnostic URL. The
              backend resolves the org from the delivery row, so an
              operator can bookmark or paste a bare delivery UUID. */}
          <Route path="/inspector/deliveries/:id" element={<DeliveryInspector />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}
